import Add from "@mui/icons-material/Add";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Edit from "@mui/icons-material/Edit";
import {
  Box,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem, GridApi } from "@mui/x-data-grid-premium";
import { CellWithSecondaryText } from "components/DataGrid";
import { ExportDataButton } from "components/ExportDataButton";
import { IssueCount } from "components/IssueCount";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import {
  formatStoredNotReportingReason,
  getOakNotReportingReason,
} from "encamp-shared/src/tierii/oakNotReportingReason";
import { getFullName } from "encamp-shared/src/utils/name";
import { gql } from "generated-graphql";
import {
  AssignmentType,
  FacilityStatus,
  FacilityTierIiReportsQuery,
  Permission,
  TierIiReportKind,
  TierIiReportOrgStatus,
  TierIiReportEncampStatus,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useFacilityReportEditable } from "hooks/useFacilityReportEditable";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useOmnisearchInput } from "hooks/useOmnisearchDatagrid/useOmnisearchInput";
import invariant from "invariant";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getChemicalsBreadcrumb } from "util/breadcrumb";
import { CombinedStatusValues, reportKindToLabel, statusToLabel } from "util/constants";
import { prettyPrintDateMed } from "util/dates";

import { useBreadcrumb } from "../../../../hooks/useBreadcrumbs";
import { AssigneesEditorDialog, AssignmentAction } from "./AssigneesEditor";
import { CreateAnnualReportDialog } from "./CreateAnnualReportDialog";
import ReportActionsMenu from "./ReportActionsMenu";
import { ReportBulkActionsMenu } from "./ReportBulkActionsMenu";

export type ReportRow = FacilityTierIiReportsQuery["facilityTierIIReports"]["items"][0];

const additionalFilterColumns: FilterKey[] = [
  {
    header: "State/Territory",
    key: "state",
  },
  {
    header: "County",
    key: "county",
  },
  {
    key: "customerFacilityId",
    header: "Facility ID",
    filterKeyType: "text",
  },
  {
    key: "facilityStatus",
    header: "Facility Status",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.values(FacilityStatus).sort(),
  },
  {
    key: "isColocated",
    header: "Is Facility Co-located?",
    filterKeyType: "boolean",
  },
  {
    key: "facilityTags",
    header: "Facility Tags",
    filterKeyType: "facilityTags",
  },
  {
    key: "notReportingReason",
    header: "Not Reporting Reason",
    filterKeyType: "enumAutocomplete",
    enumValues: ["closed", "co-located", "under-thresholds", "is-inactive", "without-encamp"],
    enumPresentationFunction: (value) => {
      return value.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
    },
  },
  {
    key: "filedOn",
    header: "Filed On",
    filterKeyType: "date",
  },
  {
    key: "reviewComplete",
    header: "Review Complete",
    filterKeyType: "boolean",
  },
];

const filterGroups: FilterGroup[] = [
  {
    name: "Report",
    fields: ["reportingYear", "reportKind", "status", "notReportingReason", "filedOn"],
  },
  {
    name: "Location",
    fields: ["facilityName", "state", "county"],
  },
  {
    name: "Assignees / Reviewers",
    fields: ["assignedTo", "reviewer", "reviewComplete"],
  },
  {
    name: "Facility Information",
    fields: ["isColocated", "customerFacilityId", "facilityStatus", "facilityTags"],
  },
];

export const REPORTS_QUERY = gql(`
    query FacilityTierIIReports($tenantId: String!, $search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
        facilityTierIIReports(tenantId: $tenantId, search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
            count
            items {
              viewId
              facilityId
              customerFacilityId
              facilityName
              facilityDateClosed
              facilityDateInactive
              tierIIReportId
              reportingYear
              reportKind
              organizationStatus
              encampStatus
              streetAddress1
              streetAddress2
              city
              county
              state
              zip
              tenantId
              assignees {
                id
                person {
                  id
                  first
                  last
                }
                assignmentType
                isComplete
              }
              reviewers {
                id
                person {
                  id
                  first
                  last
                }
                assignmentType
                isComplete
              }
              organizationStatusAnnual
              encampStatusAnnual
              hasOpenRevision
              hasVerifiedRevision
              hasOpenInitial
              hasVerifiedInitial
              reportUpdatedAt
              facilityStatus
              colocationReportingFacilityId
              isNotReporting
              isNotReportingThroughEncamp
              notReportingReason
              notReportingReasonNote
              reportUpdatedByUser {
                id
                email
                person {
                  first
                  last
                }
              }
              issueCount
              filedOn
            }
        }
    }
`);

export function ReportsTable() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasPermissionForFacility } = useAuthorization();
  const { tenantId } = useParams<{ tenantId: string }>();
  const [selectedRows, setSelectedRows] = useState<ReportRow[]>([]);
  const gridApiRef = useRef<GridApi | null>(null);

  const handleYearPickerChange = useCallback((year: string | undefined) => {
    // Clear selected rows when reporting year changes to ensure consistency
    setSelectedRows([]);
    // Also clear the grid's internal selection model
    if (gridApiRef.current) {
      gridApiRef.current.setRowSelectionModel({
        type: "include",
        ids: new Set(),
      });
    }
  }, []);

  const handleClearSelections = useCallback(() => {
    // Clear selected rows (used by bulk actions after successful operations)
    setSelectedRows([]);
  }, []);
  const [assigneesReportAndType, setAssigneesReportAndType] = useState<
    [ReportRow, AssignmentType] | undefined
  >(undefined);
  const [createAnnualReportFor, setCreateAnnualReportFor] = useState<ReportRow | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  invariant(tenantId, "tenantId is required");

  useBreadcrumb([
    {
      label: "Chemicals",
      to: tenantId ? getChemicalsBreadcrumb(tenantId) : undefined,
    },
    {
      label: "Reports",
    },
  ]);

  const openCreateAnnualReportDialog = useCallback((report: ReportRow) => {
    setCreateAnnualReportFor(report);
  }, []);

  const { canAddAssigneesOrReviewers } = useFacilityReportEditable();

  const columns: OmnisearchGridColDef<ReportRow>[] = useMemo(
    () => [
      {
        field: "facilityName",
        headerName: "Facility",
        flex: isMobile ? 0.5 : 1.1,
        filterKeyType: "facility",
        renderCell: (params) => (
          <CellWithSecondaryText
            primaryValue={
              params.row.customerFacilityId
                ? `${params.row.facilityName} (${params.row.customerFacilityId})`
                : params.row.facilityName
            }
            secondaryValue={`${
              params.row.streetAddress1 ? params.row.streetAddress1 : ""
            } ${params.row.streetAddress2 ? params.row.streetAddress2 : ""}, ${
              params.row.city ? `${params.row.city}, ` : ""
            }${params.row.state ? `${params.row.state} ` : ""}${
              params.row.zip ? `${params.row.zip}` : ""
            }${params.row.county ? ` ${params.row.county}` : ""}`}
          />
        ),
      },
      {
        field: "reportingYear",
        headerName: "Reporting Year",
        flex: isMobile ? 0.2 : 0.45,
        filterKeyType: "enum",
        enumValues: (() => {
          // last 10 years
          const currentYear = new Date().getFullYear();
          const numberOfOptions = 10;
          const years = Array.from({ length: numberOfOptions }, (_, i) => String(currentYear - i));
          return [...years, "all"];
        })(),
      },
      {
        field: "reportKind",
        headerName: "Type",
        flex: isMobile ? 0.3 : 0.3,
        valueFormatter: (value, row) =>
          value ? reportKindToLabel(value, row.state) : "Annual report not yet open",
        colSpan: (value) => (value ? undefined : 5),
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(TierIiReportKind),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.5,
        renderCell: (params) => (
          <CellWithSecondaryText
            primaryValue={
              params.row.organizationStatus &&
              params.row.encampStatus &&
              statusToLabel(params.row.organizationStatus, params.row.encampStatus)
            }
            secondaryValue={
              params.row.organizationStatus === TierIiReportOrgStatus.NotReporting
                ? // Prefer the stored reason; fall back to deriving from facility fields
                  // for historical reports that have no stored reason yet.
                  (formatStoredNotReportingReason(params.row.notReportingReason) ??
                  getOakNotReportingReason({ ...params.row, status: params.row.facilityStatus }))
                : params.row.encampStatus === TierIiReportEncampStatus.FilingComplete &&
                    params.row.filedOn
                  ? `Filed on ${new Date(params.row.filedOn).toLocaleDateString()}`
                  : undefined
            }
          />
        ),
        filterKeyType: "enumAutocomplete",
        enumValues: CombinedStatusValues,
        ignoreStateAbbreviations: true,
      },
      {
        field: "assignedTo",
        headerName: "Assigned To",
        flex: 0.6,
        sortable: false,
        linkable: false,
        renderCell: (params) => {
          const assignees = params.row.assignees ?? [];
          if (assignees.length) {
            return (
              <Stack direction="column" width="100%">
                {assignees.map((assignee) => (
                  <Typography variant="body2" key={assignee.id} width="100%" noWrap>
                    {getFullName(assignee.person)}
                  </Typography>
                ))}
              </Stack>
            );
          } else if (canAddAssigneesOrReviewers(params.row)) {
            return (
              <Link
                underline="none"
                onClick={(e) => {
                  setAssigneesReportAndType([params.row, AssignmentType.Assignee]);
                  e.stopPropagation();
                }}
                noWrap
              >
                Add Assignees
              </Link>
            );
          }
        },
      },
      {
        field: "reviewer",
        headerName: "Reviewers",
        renderHeader: (params) => {
          return (
            <Typography fontWeight="medium" fontSize="14px" sx={{ pl: "18px" }} noWrap>
              {params.colDef.headerName}
            </Typography>
          );
        },
        flex: 0.6,
        sortable: false,
        linkable: false,
        renderCell: (params) => {
          const reviewers = params.row.reviewers ?? [];
          if (reviewers.length) {
            return (
              <Stack direction="column" width="100%">
                {reviewers.map((reviewer) => (
                  <Stack
                    width="100%"
                    direction="row"
                    key={reviewer.id}
                    alignItems="center"
                    gap={theme.spacing(0.5)}
                  >
                    <CheckCircle
                      color="success"
                      sx={{
                        fontSize: "0.9rem",
                        visibility: reviewer.isComplete ? "visible" : "hidden",
                      }}
                    />
                    <Typography variant="body2" noWrap>
                      {getFullName(reviewer.person)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            );
          } else if (canAddAssigneesOrReviewers(params.row)) {
            return (
              <Link
                underline="none"
                sx={{ pl: "18px" }}
                onClick={(e) => {
                  setAssigneesReportAndType([params.row, AssignmentType.Reviewer]);
                  e.stopPropagation();
                }}
                noWrap
              >
                Add Reviewers
              </Link>
            );
          }
        },
      },
      {
        field: "reportUpdatedAt",
        headerName: "Last Updated",
        flex: 0.5,
        renderCell: (params) => (
          <CellWithSecondaryText
            primaryValue={
              params.row.reportUpdatedAt && prettyPrintDateMed(params.row.reportUpdatedAt)
            }
            secondaryValue={
              params.row.reportUpdatedByUser?.person
                ? getFullName(params.row.reportUpdatedByUser?.person)
                : params.row.tierIIReportId
                  ? "Encamp"
                  : ""
            }
          />
        ),
      },
      {
        field: "issueCount",
        headerName: "Issues",
        renderCell: ({ row }) => {
          if (
            row.organizationStatus !== TierIiReportOrgStatus.Verified &&
            row.organizationStatus !== TierIiReportOrgStatus.NotReporting
          ) {
            return <IssueCount issueCount={row.issueCount} />;
          } else {
            return <></>;
          }
        },
        flex: 0.5,
      },
      {
        field: "actions",
        type: "actions",
        getActions: (params) => {
          return getRowActions(params.row);
        },
      },
    ],
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
    [isMobile, canAddAssigneesOrReviewers, theme]
  );

  const getItems = useCallback(
    (data: FacilityTierIiReportsQuery) =>
      data.facilityTierIIReports.items.map((item: ReportRow) => ({
        ...item,
        id: item.viewId,
      })),
    []
  );

  const getCount = useCallback(
    (data: FacilityTierIiReportsQuery) => data.facilityTierIIReports.count,
    []
  );

  const getRowActions = useCallback(
    (row: ReportRow) => {
      const actions = [];

      if (!row.tierIIReportId && row.reportingYear >= currentTierIIReportingYear - 1) {
        actions.push(
          <Tooltip title="Create Report" key="createReport">
            <GridActionsCellItem
              onClick={() => openCreateAnnualReportDialog(row)}
              label="Open Annual Report"
              icon={<Add />}
            />
          </Tooltip>
        );
      } else if (row.tierIIReportId) {
        const tooltip =
          row.organizationStatus === TierIiReportOrgStatus.Verified ? "View Report" : "Edit Report";
        actions.push(
          <Tooltip title={tooltip} key="editReport">
            <IconButton
              size="small"
              href={`${window.location.pathname}/${row.tierIIReportId}`}
              aria-label={tooltip}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      }

      const canModifyReport = hasPermissionForFacility(row.facilityId, [
        Permission.VerifyTierIiReport,
      ]);

      if (canModifyReport) {
        actions.push(
          <ReportActionsMenu
            key="moreActions"
            onEditAssignees={(report, assignmentType) => {
              if (report) {
                setAssigneesReportAndType([report, assignmentType]);
              }
            }}
            facilityTierIiReport={row}
            createNewReport={openCreateAnnualReportDialog}
          />
        );
      }

      return actions;
    },
    [openCreateAnnualReportDialog, hasPermissionForFacility]
  );

  const { omnisearch, getOmnisearchValue } = useOmnisearchInput({
    isURLDriven: true,
  });

  const currentSearch = omnisearch && omnisearch.trim().length > 0 ? omnisearch.trim() : undefined;

  const reportingYearRaw = getOmnisearchValue("reportingYear");
  const exportReportingYear = useMemo(() => {
    // Only returned `undefined` for `all`.
    if (reportingYearRaw === "all") {
      return undefined;
    }

    // ast-grep-ignore: no-unsafe-number-parsing
    const parsed = Number(reportingYearRaw);
    // Default to the `currentTierIIReportingYear` if the parsed value is NaN.
    // Unless explicity set to all a reporting year must be used
    return !Number.isNaN(parsed) ? parsed : currentTierIIReportingYear;
  }, [reportingYearRaw]);

  const exportSearchFilter = useMemo(() => {
    // This ensures that the filter that's sent to the export job
    // always has a reporting year. If an omnisearch search string already exists
    // eg: free text search
    // and it is missing the reporting year it will add it in

    let search = currentSearch ? currentSearch : "";

    if (!search.includes("reportingYear:")) {
      search = `reportingYear:${exportReportingYear} ${search}`;
    }

    return `chemicalFeatureFlagOverride:!DISABLED ${search}`.trim();
  }, [exportReportingYear, currentSearch]);

  const defaultSearch = useMemo(() => {
    const flagFilter = "chemicalFeatureFlagOverride:!DISABLED";
    const reportingYearFilter = `reportingYear:${currentTierIIReportingYear}`;
    if (!reportingYearRaw) {
      // Filter the current reporting year if the user has not selected a year
      return `${flagFilter} ${reportingYearFilter}`;
    }
    return flagFilter;
  }, [reportingYearRaw]);

  return (
    <Box sx={{ py: 3 }}>
      <OmnisearchDataGrid
        apiRef={gridApiRef}
        persistenceKey="reports-table"
        isURLDriven
        columns={columns}
        rowHeight="auto"
        dataQuery={REPORTS_QUERY}
        dataQueryVariables={{
          tenantId: tenantId ?? "",
        }}
        defaultSearch={defaultSearch}
        yearPickerKey="reportingYear"
        yearPickerDefaultValue={String(currentTierIIReportingYear)}
        yearPickerShowAllOption
        onYearPickerChange={handleYearPickerChange}
        getItems={getItems}
        getCount={getCount}
        noDataMessage="No facilities have been added to this organization yet."
        onRowClick={({ row }, event) => {
          if (row.tierIIReportId) {
            navigate(row.tierIIReportId);
          }
        }}
        getRowLink={(row) => (row.tierIIReportId ? `./${row.tierIIReportId}` : null)}
        initialSortModel={[{ field: "facilityName", sort: "asc" }]}
        onSelectedRowsChanged={setSelectedRows}
        isRowSelectable={({ row }) =>
          !!row && hasPermissionForFacility(row.facilityId, [Permission.VerifyTierIiReport])
        }
        commandButtons={[
          <ReportBulkActionsMenu
            key="bulk-actions"
            reports={selectedRows}
            onClearSelections={handleClearSelections}
          />,
          <ExportDataButton
            key="export-reports"
            tenantId={tenantId}
            exportType="chemicalReports"
            title="Export"
            gridSettingsKey="reports-table"
            fileName={`chemical-reports-${exportReportingYear}`}
            reportingYear={exportReportingYear}
            filter={{
              search: exportSearchFilter,
            }}
          />,
        ]}
        additionalFilterColumns={additionalFilterColumns}
        filterGroups={filterGroups}
        keyAliases={{
          facilityTags: "facilityTags",
        }}
        columnVisibilityModel={{
          ...(isMobile && {
            status: false,
            assignedTo: false,
            reviewer: false,
            reportUpdatedAt: false,
            issueCount: false,
            actions: false,
          }),
        }}
      />
      {assigneesReportAndType && (
        <AssigneesEditorDialog
          assignmentAction={AssignmentAction.Assign}
          onClose={() => setAssigneesReportAndType(undefined)}
          assignmentType={assigneesReportAndType[1]}
          tierIIReports={[assigneesReportAndType[0]]}
          tenantId={tenantId}
        />
      )}
      {createAnnualReportFor && (
        <CreateAnnualReportDialog
          onClose={() => setCreateAnnualReportFor(null)}
          reports={[createAnnualReportFor]}
        />
      )}
    </Box>
  );
}
